<header class="app-page-chrome">
  <!-- Left: breadcrumbs -->
  <div class="min-w-0 flex items-center pt-2">
    @if (dynamicTitleService.breadcrumbs().length > 0) {
      <div class="flex min-w-0 items-center space-x-2 text-[14px] sm:text-[16px]">
        <a
          [routerLink]="['/admin', 'home']"
          class="font-spaceGrotesk text-support-text hover:underline transition-opacity duration-200 whitespace-nowrap"
        >
          Home
        </a>
        @for (crumb of dynamicTitleService.breadcrumbs(); track crumb.label; let last = $last) {
          <span class="text-base-200">/</span>
          @if (crumb.link && !last) {
            <a
              [routerLink]="crumb.link"
              class="font-spaceGrotesk text-support-text hover:underline transition-opacity duration-200 whitespace-nowrap"
            >
              {{ crumb.label }}
            </a>
          } @else {
            <span class="font-spaceGrotesk font-semibold text-inverted-neutral truncate">
              {{ crumb.label }}
            </span>
          }
        }
      </div>
    }
  </div>

  <!-- Right: brand chip + user pill + menu button -->
  <div class="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-3">
    <div
      [routerLink]="['/admin', 'home']"
      class="brand-chip shrink-0"
      matRipple
    >
      <app-logo class="brand-chip-mark" [clickable]="false"></app-logo>
      <span class="brand-chip-label whitespace-nowrap">
        Kekeno Surveys
      </span>
    </div>
    <button
      (click)="toggleMenu()"
      type="button"
      [attr.aria-label]="hasUnreadNotifications() ? 'Menu, unread notifications' : 'Menu'"
      class="relative shrink-0 bg-transparent border border-nav-btn-border text-nav-btn-text hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text px-5 py-2 rounded-full inter-500 text-sm transition duration-150 ease-out active:scale-[0.98] focus:outline-none"
    >
      <span>Menu</span>
      @if (hasUnreadNotifications()) {
        <span
          class="absolute right-1 top-0 h-2.5 w-2.5 translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-lavender ring-2 ring-base-100 shadow-[0_0_0_1px_rgba(192,149,255,0.34)]"
          aria-hidden="true"
        ></span>
      }
    </button>
  </div>
</header>

<!-- Optional tabs row projected from parent -->
<ng-content select="[navbarTabs]"/>

<!-- Overlay -->
@if (menuOpen) {
  <div class="fixed inset-0 bg-black/45 z-40" (click)="closeMenu()"></div>
}

<!-- Side Menu Panel -->
<aside class="side-menu-panel w-[300px] max-w-[85vw] duration-300"
     [class.translate-x-full]="!menuOpen"
     [class.translate-x-0]="menuOpen">

  <!-- Panel header -->
  <div class="side-menu-header">
    <div class="flex flex-col">
      <span class="font-spaceGrotesk font-bold text-xl">Menu</span>
      <div class="mt-1" title="Current User and Account">
        <span class="font-spaceGrotesk text-[13px] opacity-75">{{ currentUserFirstName() || 'User' }} · {{ currentAccountName() || 'Account' }}</span>
      </div>
    </div>
    <button (click)="closeMenu()" type="button"
      class="side-menu-close-button">
      Close
    </button>
  </div>

  <!-- Nav items -->
  <nav class="flex-1 overflow-y-auto px-4 py-4 space-y-1" style="-webkit-overflow-scrolling: touch; overscroll-behavior: contain;">
    <ng-content/>

    <a [routerLink]="['/admin', 'home']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">home</span>
      <span class="inter-400 text-[1.125rem]">Home</span>
    </a>

    <button
      type="button"
      (click)="openStartQuestionnaireDialog()"
      class="side-menu-item"
    >
      <span class="material-symbols-outlined">play_arrow</span>
      <span class="inter-400 text-[1.125rem]">Start Questionnaire</span>
    </button>

    <a [routerLink]="['/admin', 'respondents']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">person</span>
      <span class="inter-400 text-[1.125rem]">Respondents</span>
    </a>

    <a [routerLink]="['/admin', 'forms']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">edit_note</span>
      <span class="inter-400 text-[1.125rem]">Manage Forms</span>
    </a>

    <a [routerLink]="['/admin', 'question-sets']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">library_books</span>
      <span class="inter-400 text-[1.125rem]">Question Set Library</span>
    </a>

    <a [routerLink]="['/admin', 'analytics']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">pie_chart</span>
      <span class="inter-400 text-[1.125rem]">Analytics</span>
    </a>

    <a [routerLink]="['/notifications']" (click)="closeMenu()"
       [attr.aria-label]="hasUnreadNotifications() ? 'Notifications, unread notifications' : 'Notifications'"
       class="side-menu-item">
      <span class="inline-flex">
        <span class="material-symbols-outlined">notifications</span>
      </span>
      <span class="inter-400 text-[1.125rem]">Notifications</span>
      @if (hasUnreadNotifications()) {
        <span
          class="ml-auto inline-flex h-3 w-3 rounded-full bg-brand-lavender ring-2 ring-brand-ink-soft shadow-[0_0_0_1px_rgba(192,149,255,0.34)]"
          aria-hidden="true"
        ></span>
      }
    </a>

    <a [routerLink]="['/admin', 'intents']" (click)="closeMenu()"
       class="side-menu-item">
      <span class="material-symbols-outlined">psychology</span>
      <span class="inter-400 text-[1.125rem]">Intents</span>
    </a>

    @if (canViewUsers()) {
      <a [routerLink]="['/admin', 'users']" (click)="closeMenu()"
         class="side-menu-item">
        <span class="material-symbols-outlined">groups</span>
        <span class="inter-400 text-[1.125rem]">Users</span>
      </a>
    }

    @if (canViewQuestionnaireInstances()) {
      <a [routerLink]="['/admin', 'questionnaire-instances']" (click)="closeMenu()"
         class="side-menu-item">
        <span class="material-symbols-outlined">troubleshoot</span>
        <span class="inter-400 text-[1.125rem]">Questionnaire Instances</span>
      </a>
    }

  </nav>

  <!-- Bottom utility section -->
  <div class="side-menu-utility-section">
    <a [routerLink]="['/admin', 'account']" (click)="closeMenu()"
      class="side-menu-item">
      <span class="material-symbols-outlined">manage_accounts</span>
      <span class="inter-400 text-[1.125rem]">Account Settings</span>
    </a>

    <button (click)="toggleTheme(); closeMenu()" type="button"
      class="side-menu-item">
      @if (!isDarkTheme()) {
        <svg class="fill-current w-5 h-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z"/>
        </svg>
      }
      @if (isDarkTheme()) {
        <svg class="fill-current w-5 h-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z"/>
        </svg>
      }
      <span class="inter-400 text-[1.125rem]">Toggle Theme</span>
    </button>

    <button (click)="onClickSwitch(); closeMenu()" type="button"
      class="side-menu-item">
      <span class="material-symbols-outlined">swap_horiz</span>
      <span class="inter-400 text-[1.125rem]">Switch User</span>
    </button>

    <button (click)="logout(); closeMenu()" type="button"
      class="side-menu-item">
      <span class="material-symbols-outlined">logout</span>
      <span class="inter-400 text-[1.125rem]">Logout</span>
    </button>
  </div>
</aside>
